import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiTrash2 } from 'react-icons/fi';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TrialWipePanel({ token }) {
  const [counts, setCounts] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const loadCounts = () => {
    fetch(`${API}/api/feedback-app/${token}/trial-counts`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setCounts)
      .catch(() => setCounts(null));
  };

  useEffect(() => { loadCounts(); /* eslint-disable-next-line */ }, [token]);

  const wipe = async () => {
    setError('');
    if (confirmText !== 'DELETE_ALL_TRIAL') {
      setError('Type DELETE_ALL_TRIAL exactly (all caps, no spaces).');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/feedback-app/${token}/wipe-trial-data`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: 'DELETE_ALL_TRIAL' }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Wipe failed'); }
      const data = await r.json();
      setResult(data.deleted);
      setShowModal(false);
      setConfirmText('');
      loadCounts();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const total = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="bg-red-900/20 border border-red-500/40 rounded-xl p-4" data-testid="trial-wipe-panel">
      <div className="flex items-start gap-2 mb-2">
        <FiAlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-red-300 text-[10px] tracking-widest uppercase font-semibold">Danger Zone — Trial Reset</p>
          <p className="text-white/70 text-xs mt-1">Before going live with staff, wipe all trial ratings, published reviews, villa photos and thank-you coupons.</p>
        </div>
      </div>

      {counts && (
        <div className="bg-[#0F1420] rounded-lg p-3 text-xs mb-3 space-y-1" data-testid="trial-counts">
          <div className="flex justify-between text-white/80"><span>Feedback submissions</span><span className="font-bold text-[#F57C20]">{counts.feedback_submissions}</span></div>
          <div className="flex justify-between text-white/80"><span>Published reviews</span><span className="font-bold text-[#F57C20]">{counts.published_reviews}</span></div>
          <div className="flex justify-between text-white/80"><span>Villa photo batches</span><span className="font-bold text-[#F57C20]">{counts.villa_photo_batches}</span></div>
          <div className="flex justify-between text-white/80"><span>Thank-you coupons</span><span className="font-bold text-[#F57C20]">{counts.thankyou_coupons}</span></div>
          <div className="flex justify-between text-white pt-1 border-t border-white/10"><span className="font-semibold">Total rows to delete</span><span className="font-bold text-red-400">{total}</span></div>
        </div>
      )}

      {result && (
        <div className="bg-green-900/30 border border-green-500/40 text-green-300 text-xs px-3 py-2 rounded mb-2" data-testid="wipe-result">
          Wiped: {result.feedback_submissions} feedbacks · {result.published_reviews} reviews · {result.villa_photo_batches} photo batches ({result.storage_objects} objects) · {result.thankyou_coupons} coupons
        </div>
      )}

      <button onClick={() => { setShowModal(true); setResult(null); setError(''); }}
        disabled={total === 0}
        data-testid="open-wipe-modal-btn"
        className="w-full bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-2.5 rounded flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
        <FiTrash2 className="w-4 h-4" /> {total === 0 ? 'Nothing to wipe' : `Wipe All Trial Data (${total})`}
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="wipe-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="bg-[#1B2432] rounded-xl p-5 max-w-sm w-full border border-red-500/50">
            <div className="flex items-start gap-2 mb-3">
              <FiAlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-semibold text-sm">This cannot be undone</p>
                <p className="text-white/60 text-xs mt-1">
                  Permanently deleting <span className="text-red-400 font-bold">{total} rows</span> across feedbacks, reviews, photos and coupons. Photo files will also be deleted from storage.
                </p>
              </div>
            </div>
            <label className="block text-white/70 text-xs mb-1">Type <span className="text-red-400 font-mono font-bold">DELETE_ALL_TRIAL</span> to confirm:</label>
            <input type="text" value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              data-testid="wipe-confirm-input"
              className="w-full bg-[#0F1420] text-white text-sm px-3 py-2 rounded border border-red-500/30 font-mono mb-3"
              autoFocus />
            {error && <p className="text-red-400 text-xs mb-2" data-testid="wipe-error">{error}</p>}
            <div className="flex gap-2">
              <button onClick={() => { setShowModal(false); setConfirmText(''); setError(''); }}
                data-testid="wipe-cancel-btn"
                className="flex-1 bg-white/10 hover:bg-white/20 text-white text-sm py-2 rounded">Cancel</button>
              <button onClick={wipe} disabled={busy || confirmText !== 'DELETE_ALL_TRIAL'}
                data-testid="wipe-confirm-btn"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-2 rounded disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? 'Wiping…' : 'Wipe Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
